import { X } from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useTranslation } from '../use-translation.js';

export type SidebarWidth = number | `${number}%`;

export interface SidebarProps {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly id?: string;
  readonly width?: SidebarWidth;
  readonly position?: 'left' | 'right';
}

const DEFAULT_MAX_WIDTH = 384;
const SIDEBAR_ANIMATION_DURATION_MS = 200;

export function Sidebar({
  title,
  onClose,
  children,
  id = 'sidebar',
  width = DEFAULT_MAX_WIDTH,
  position = 'right',
}: SidebarProps): React.JSX.Element {
  const { t } = useTranslation();
  const [isClosing, setIsClosing] = useState(false);
  const titleId = `${id}-title`;
  const positionClass = position === 'left' ? 'left-0' : 'right-0';
  const animationClass = `sidebar-${isClosing ? 'exit' : 'enter'}-${position}`;
  const backdropClass = isClosing ? 'sidebar-backdrop-exit' : 'sidebar-backdrop-enter';
  const requestClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') requestClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [requestClose]);

  useEffect(() => {
    if (!isClosing) return undefined;

    const timeoutId = window.setTimeout(onClose, SIDEBAR_ANIMATION_DURATION_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isClosing, onClose]);

  return (
    <div className="fixed inset-0 z-50 w-full">
      <button
        type="button"
        aria-label={`${t('shared.close')} ${title}`}
        className={`absolute inset-0 bg-black/40 backdrop-blur-[1px] ${backdropClass}`}
        onClick={requestClose}
      />
      <aside
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width: '95%', maxWidth: width }}
        className={`absolute inset-y-0 flex flex-col bg-surface shadow-overlay ${positionClass} ${animationClass}`}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 id={titleId} className="text-lg font-bold text-foreground">
            {title}
          </h2>
          <button
            type="button"
            aria-label={`${t('shared.close')} ${title}`}
            onClick={requestClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      </aside>
    </div>
  );
}
